import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The two user-facing module layers (segmented toggle). An `as const` object so
 * call sites reference `ModuleLayer.Advisor` / `ModuleLayer.Client` instead of
 * bare string literals — same pattern as the API-derived enums.
 */
export const ModuleLayer = {
	Advisor: "advisor",
	Client: "client",
} as const;
export type ModuleLayer = (typeof ModuleLayer)[keyof typeof ModuleLayer];

type Props = {
	value: ModuleLayer;
	onChange: (next: ModuleLayer) => void;
	advisorHint?: string;
	clientHint?: string;
};

/**
 * Segmented toggle Poradenská / Klientská — header bar that sits above every
 * module screen. MVP: ships with the toggle visible but the "Klientská" view
 * is just a placeholder; the entire data contract is ready for it.
 */
export const ModuleLayerToggle = ({
	value,
	onChange,
	advisorHint,
	clientHint,
}: Props) => {
	const { t } = useResource();
	const hint = value === "advisor" ? advisorHint : clientHint;

	return (
		<div className="flex items-center justify-between gap-4 border-b bg-muted/30 px-5 py-3">
			<p className="text-muted-foreground text-sm">
				{hint ?? t(Resources.Modeling.Common.LayerToggleHint)}
			</p>
			<div className="bg-background inline-flex items-center rounded-md border p-0.5">
				{[ModuleLayer.Client, ModuleLayer.Advisor].map((layer) => (
					<Button
						key={layer}
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => onChange(layer)}
						className={cn(
							"h-7 rounded-sm px-3 text-xs font-medium",
							value === layer
								? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						{layer === ModuleLayer.Client
							? t(Resources.Modeling.Common.ClientView)
							: t(Resources.Modeling.Common.AdvisorView)}
					</Button>
				))}
			</div>
		</div>
	);
};
